import ReactDOM from "react-dom";
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState, ReactElement} from 'react';
import {observer} from "mobx-react-lite";
import classNames from "classnames";
import {useAppState, useRootStore} from "@stores/AppStateContext";

import {MenuAlignment, MenuPlacement, normalizePosition} from "../../../utils/normalizePosition";
import {Pointer, PointerPosition} from "../../components/Pointer/Pointer";
import {HEADER_MENU_HEIGHT} from "@stores/constants";

import {editorContextRootRef, editorScrollRootRef} from '../EditorReact';
import {leftMenuContextRootRef, leftMenuScrollRootRef} from '../ListView';
import {screenSizeProvider} from "@stores/ScreenSizeProvider";


import './ctx-menu-position.css';


export interface ContextMenuParams {
    [x: string]: any;
}

export interface ContextMenuAdditionalParamsType {
    [x: string]: any;
}

export type MousePositionParams = {
    clientX: number,
    clientY: number,
} | {
    getTargetBoundingBox: () => DOMRect;
}

export interface ContextMenuProps {
    type: ContextMenuType,
    id: string,
    clientX?: number,
    clientY?: number,
    isDark?: boolean,
    params?: ContextMenuParams,
    onClose?: () => void,
    menuPlacement?: MenuPlacement;
    menuAlignment?: MenuAlignment;
    withPointer?: boolean;
    isPointerLarge?: boolean;
    getTargetBoundingBox?: () => DOMRect | undefined;
    hotKeys?: Record<string, () => void>;
    VERTICAL_SHIFT?: number,
    HORIZONTAL_SHIFT?: number,
    minWidth?: number,
    maxWidth?: number,
    withOverlay?: boolean,
    shouldForcePlace?: boolean,
}

export type ContextMenuType =
    'item'
    | 'task'
    | 'avatar'
    | 'document'
    | 'workspace'
    | 'workspace-row'
    | 'add-to-cluster'
    | 'editor-text'
    | 'editor-trigger'
    | 'editor-table'
    | 'editor-link'
    | 'editor-image'
    | 'editor-plus'
    | 'editor-embed'
    | 'editor-callout'
    | 'notification-centre'
    | 'select-options'
    | 'calendar-select-options'
    | 'calendar-select-repeat-options'
    | 'quiz'
    | 'quiz-question'
    | 'slide'
    | 'acknowledgments-overview'
    | 'avatar-group-users'
    | 'taskList'
    | 'taskListUnit'
    | 'taskListUnit-select-options'
    | 'taskList-schedule'
    | 'tree-quick-actions'
    | 'profile'

const contextMenuHTML = window.document.getElementById('contextMenu')!;
const contextMenuLowHTML = window.document.getElementById('contextMenuLow')!;

const editorContextsTriggeredWithoutRightClickEvent = new Set([
    'editor-text',
    'editor-trigger',
    'editor-link',
]);

const editorContexts = new Set([
    ...editorContextsTriggeredWithoutRightClickEvent,
    'editor-table',
    'editor-plus',
    'editor-image',
    'editor-embed',
    'editor-callout',
]);

export const getRenderingScope = (type: ContextMenuType, params: any): {
    renderScope: HTMLElement;
    scrollScope: HTMLElement; /* currently scrollTop is reflected in the renderScope's position shift, so this element is not needed here */
} => {
    switch (type) {
        case 'editor-text':
        case 'editor-trigger':
        case 'editor-plus':
        case 'editor-link':
        case 'editor-table':
        case 'editor-image':
        case 'add-to-cluster':
        case 'document':
            return {
                renderScope: editorContextRootRef.current!,
                scrollScope: editorScrollRootRef.current!,
            };
        case 'item':
            if (params.inEditorView === true) {
                return {
                    renderScope: editorContextRootRef.current!,
                    scrollScope: editorScrollRootRef.current!,
                };
            } else {
                return {
                    renderScope: leftMenuContextRootRef.current!,
                    scrollScope: leftMenuScrollRootRef.current!,
                };
            }
        case 'taskList-schedule':
            return {
                renderScope: contextMenuLowHTML,
                scrollScope: window.document.documentElement,
            };
        case 'quiz':
        case 'quiz-question':
        default:
            return {
                renderScope: contextMenuHTML,
                scrollScope: window.document.documentElement,
            };
    }
}

/* if context menu opens automatically on click in editor we need a catch-up to avid closing it on the same click handle */
export const isTriggeredBySelectionChangeInEditor = (contextMenu: ContextMenuProps | null) =>
    contextMenu !== null
    && editorContextsTriggeredWithoutRightClickEvent.has(contextMenu.type);
export const isEditorContext = (contextMenu: ContextMenuProps | null) => contextMenu !== null && editorContexts.has(contextMenu.type);


export interface MenuPosition {
    params: any,
    actualDirection: MenuPlacement,
    style: {},
    pointer?: PointerPosition;
}

type RenderFunction = (type: ContextMenuType, params: any, handleClose: any, isDark?: boolean) => ReactElement | null;

export interface ContextMenuManagerProps {
    renderContextMenu: RenderFunction;
    renderScope?: HTMLElement;
}

const ContextMenuManagerBase = ({renderContextMenu, renderScope}: ContextMenuManagerProps) => {
    const appState = useAppState();

    const isClickInsideRef = useRef<boolean>(false);

    const isInContextMenuClick = (clickedTarget: HTMLElement) => {
        return !!(
            clickedTarget.closest('.ctx-menu-position')
            || (isTriggeredBySelectionChangeInEditor(appState.contextMenu) && clickedTarget.closest('.ER-wrapper'))
        )
    }
    const {
        type,
        params,
        withOverlay = false,
    } = appState.contextMenu || {};

    const handleClose = useCallback(() => {
        if (appState.contextMenu === null) {
            return;
        }
        appState.setContextMenu(null);
    }, []);

    const onDocumentMouseDown = useCallback((event: MouseEvent) => {
        const clickedTarget: HTMLElement = event.target as HTMLElement;

        isClickInsideRef.current = isInContextMenuClick(clickedTarget);
    }, [isClickInsideRef]);

    const onDocumentClick = useCallback((event: MouseEvent): void => {
        if (event.defaultPrevented) {
            return;
        }
        const clickedTarget: HTMLElement = event.target as HTMLElement;

        if (isClickInsideRef.current || isInContextMenuClick(clickedTarget)) {
            return;
        }

        handleClose();
    }, [handleClose, isClickInsideRef]);

    useEffect(() => {
        document.addEventListener('click', onDocumentClick);
        document.addEventListener('mousedown', onDocumentMouseDown);
        return () => {
            document.removeEventListener('click', onDocumentClick);
            document.removeEventListener('mousedown', onDocumentMouseDown);
        }
    }, [onDocumentClick, onDocumentMouseDown]);

    const stateCtxMenu = appState.contextMenu;
    const menus: ContextMenuProps[] = [];
    if (stateCtxMenu) menus.push(stateCtxMenu);
    menus.push(...appState.additionalContextMenus);

    return (
        ReactDOM.createPortal(
            <>
                {menus.map((menu, idx) => {
                    const contentFor = (m: ContextMenuProps) =>
                        renderContextMenu(
                            m.type,
                            m.params,
                            m === stateCtxMenu ? handleClose : appState.removeAdditionalContextMenu,
                            m.isDark
                        );

                    const content = contentFor(menu);
                    if (!content) return null;

                    return (
                        <CtxMenu
                            key={menu.id}
                            renderScope={renderScope}
                            ctxMenu={menu}
                            content={content}
                            layerIndex={idx}
                        />
                    );
                })}
            </>
            , renderScope ? renderScope : getRenderingScope(type!, params).renderScope
        )
    )
}

export const ContextMenuManager = observer(ContextMenuManagerBase);

export interface CtxMenuProps {
    ctxMenu: ContextMenuProps;
    content: ReactElement;
    renderScope?: HTMLElement;
    layerIndex?: number;
}

const Z_STEP = 50;
const Z_MENU_BASE = 4500;

export const CtxMenu = (
    {
        ctxMenu,
        content,
        renderScope,
        layerIndex = 0,
    }: CtxMenuProps
) => {
    const appState= useAppState();
    const rootStore = useRootStore();

    const [menuPosition, setMenuPosition] = useState<MenuPosition | undefined>(undefined);
    const {
        type,
        params,
        isDark = false,
        isPointerLarge = false,
        minWidth,
        maxWidth,
        shouldForcePlace = false,
        withOverlay = false,
    } = ctxMenu || {};
    const zMenu = Z_MENU_BASE + layerIndex * Z_STEP;
    const zOverlay = zMenu - 10;

    const prevWidthRef = useRef<number>(0);
    const [recalculationCount, forceUpdate] = useState<number>(0);
    const contextMenuRef = useRef<HTMLDivElement | null>(null);
    const rafIdRef = useRef<number | null>(null);
    const resizeObserverCallback: ResizeObserverCallback = (entries) => {
        const size = Array.isArray(entries[0].contentBoxSize)
            ? entries[0].contentBoxSize[0].inlineSize
            : (entries[0].contentBoxSize as any)?.inlineSize ?? entries[0].contentRect.width;

        if (size !== prevWidthRef.current) {
            prevWidthRef.current = size;
            rafIdRef.current = requestAnimationFrame(() => {
                try {
                    forceUpdate(c => c + 1);
                } catch {
                }
            });
        }
    };

    useEffect(() => {
        if (!ctxMenu) return;
        const el = renderScope ?? getRenderingScope(type!, params).renderScope;
        const ro = new ResizeObserver(resizeObserverCallback);
        ro.observe(el);
        return () => {
            if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
            ro.disconnect();
        };
    }, [ctxMenu, renderScope, type, params]);

    useEffect(() => {
        if (!window.visualViewport) return;
        const handleResize = () => {
            forceUpdate(recalculationCount => recalculationCount + 1);
        };

        window.visualViewport.addEventListener('resize', handleResize);
        return () => window.visualViewport?.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (menuPosition === undefined) {
            return;
        }
        if (ctxMenu === null) {
            setMenuPosition(undefined);
        }
    }, [menuPosition, ctxMenu]);

    useLayoutEffect(
        () => {
            const {
                clientX, clientY, withPointer,
                menuPlacement, menuAlignment, getTargetBoundingBox,
                VERTICAL_SHIFT, HORIZONTAL_SHIFT, type, isPointerLarge
            } = ctxMenu || {};
            const isMobile = screenSizeProvider.view !== 'desktop';
            if (isMobile && !shouldForcePlace) {
                return;
            }
            if (!content) {
                return;
            }

            if (contextMenuRef.current !== null
                && (getTargetBoundingBox !== undefined || (clientX !== undefined && clientY !== undefined))
            ) {
                const targetBoundingBox = getTargetBoundingBox ? getTargetBoundingBox() : undefined;
                const contextMenu = contextMenuRef.current;
                const domScopes = getRenderingScope(type!, params);
                const _renderScope = renderScope ? renderScope : domScopes.renderScope;
                const newMenuPosition = normalizePosition({
                    mouseX: clientX,
                    mouseY: clientY,
                    contextMenu: contextMenu,
                    withPointer: withPointer,
                    placement: menuPlacement,
                    alignment: menuAlignment,
                    targetBoundingBox: targetBoundingBox,
                    VERTICAL_SHIFT: VERTICAL_SHIFT,
                    HORIZONTAL_SHIFT: HORIZONTAL_SHIFT,
                    getScopeBoundingClientRect: _renderScope.getBoundingClientRect.bind(_renderScope),
                    isPointerLarge: isPointerLarge,
                    scopeAdditionalPadding: isMobile ? 0 : HEADER_MENU_HEIGHT,
                });
                let {pointerPosition} = newMenuPosition;
                setMenuPosition({
                    params: params!,
                    actualDirection: newMenuPosition.actualDirection,
                    style: {
                        top: `${newMenuPosition.top}px`,
                        left: `${newMenuPosition.left}px`,
                    },
                    pointer: pointerPosition,
                });
            }

            return;
        }, [
            recalculationCount,
            menuPosition?.params,
            params,
            contextMenuRef,
            /* contextMenu can change as passed through params can change, but the window position calculations shouldn't be affected */
            ctxMenu,
            ctxMenu?.type,
            ctxMenu?.getTargetBoundingBox,
            rootStore.leftMenuWidth,
        ]
    );

    const isMobile = screenSizeProvider.view !== 'desktop';

    return <>
        {withOverlay && (
            <div
                key={`${ctxMenu.type}#${ctxMenu.id}overlay`}
                className="ctx-menu-overlay"
                style={{zIndex: zOverlay}}
            />
        )}
        <div className={classNames({
            'ctx-menu-position': true,
            "ctx-menu-position-mobile": shouldForcePlace
        })}
             key={`${ctxMenu.type}#${ctxMenu.id}`}
             ref={contextMenuRef}
             style={{
                 ...menuPosition?.style,
                 zIndex: zMenu,
                 minWidth: isMobile && !shouldForcePlace ? undefined : minWidth,
                 maxWidth: isMobile && !shouldForcePlace ? undefined : maxWidth,
             }}
             onClick={(event) => {
                 if (appState.latestAdditionalCtxMenu && appState.latestAdditionalCtxMenu!.id !== ctxMenu.id) {
                     appState.removeAdditionalContextMenu();
                 }
             }}
        >
            {content}
        </div>
        {menuPosition?.pointer ?
            <Pointer isDark={isDark} isLarge={isPointerLarge} {...menuPosition.pointer}/> : null}
    </>
}
